import * as XLSX from './secureXlsx';
import type { SaleslistImportRowPayload } from '../api/saleslistImportApi';

export function normalizeHeader(h: string): string {
  return h
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// The uploaded sheet's own tariff/rate header — 'marge per uur', 'uurtarief', 'tarief' — is not
// separable from the engine-owned configured margin. `baseRate` is the one tariff field the backend
// accepts; the margin itself is always recalculated by the pricing engine, never imported.
const IGNORED_MARGIN_HEADER = 'marge per uur';

export const headerMap: Record<string, keyof SaleslistImportRowPayload> = {
  // Title
  'titel': 'title',
  'title': 'title',
  'vacature titel': 'title',

  // Company
  'bedrijf': 'companyName',
  'klant': 'companyName',
  'opdrachtgever': 'companyName',
  'company': 'companyName',

  // Status
  'status': 'status',
  'status nl': 'status',

  // Sales owner / verkocht door
  'verkocht door': 'verkochtDoor',
  'verkoper': 'verkochtDoor',
  'sales owner': 'verkochtDoor',
  'accountmanager': 'verkochtDoor',

  // Admin type
  'admin': 'admin',
  'administratie': 'admin',
  'admin type': 'admin',

  // Profession
  'beroep': 'profession',
  'functie': 'profession',
  'functie naam': 'profession',
  'profession': 'profession',

  // Start date
  'startdatum': 'expectedStartDate',
  'start datum': 'expectedStartDate',
  'expected start date': 'expectedStartDate',

  // Description
  'omschrijving': 'description',
  'omschrijving/notes': 'description',
  'description': 'description',

  // Location city
  'plaats': 'locationCity',
  'locatie': 'locationCity',
  'city': 'locationCity',

  // Contacts
  'contact': 'primaryContactName',
  'contact naam': 'primaryContactName',
  'contact phone': 'primaryContactPhone',
  'telefoon': 'primaryContactPhone',
  'phone': 'primaryContactPhone',

  // Rates
  'uurtarief': 'baseRate',
  'tarief': 'baseRate',

  // SA flag
  'sa': 'sa',
};

type StringImportField = Exclude<keyof SaleslistImportRowPayload, 'baseRate' | 'sa' | 'expectedStartDate'>;

// Accepts "42,50" (NL decimal comma), "1.234,50" (NL thousands dot), "42.50" (plain dot decimal),
// and "€ 42,50" (currency prefix/whitespace) alike: strip everything but digits/separators/minus,
// then treat the LAST comma-or-dot as the decimal separator and drop the others as thousands
// grouping. Returns undefined (never NaN) when nothing digit-like survives the strip.
function parseTariffNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;

  const stripped = String(v).replace(/[^0-9.,-]/g, '');
  if (!stripped) return undefined;

  // A single separator is always treated as the decimal point (e.g. "12.000" → 12), never as
  // thousands grouping: hourly tariffs never reach a thousand euros.
  const lastSeparatorIndex = Math.max(stripped.lastIndexOf(','), stripped.lastIndexOf('.'));
  const normalized =
    lastSeparatorIndex === -1
      ? stripped
      : `${stripped.slice(0, lastSeparatorIndex).replace(/[.,]/g, '')}.${stripped.slice(lastSeparatorIndex + 1)}`;

  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : undefined;
}

export interface MappedImportRow {
  row: Partial<SaleslistImportRowPayload>;
  /** True when the raw row carried the ignored `marge per uur` header. */
  hasIgnoredMarginColumn: boolean;
  /** True when a `tarief`/`uurtarief` cell was non-blank but did not parse to a finite number. */
  hasUnparsedRate: boolean;
}

export function mapImportRow(rawRow: Record<string, unknown>): MappedImportRow {
  const row: Partial<SaleslistImportRowPayload> = {};
  let hasIgnoredMarginColumn = false;
  let hasUnparsedRate = false;

  for (const [k, v] of Object.entries(rawRow)) {
    const key = normalizeHeader(k);
    if (key === IGNORED_MARGIN_HEADER) {
      hasIgnoredMarginColumn = true;
      continue;
    }

    const target = headerMap[key];
    if (!target) continue;

    if (target === 'baseRate') {
      const num = parseTariffNumber(v);
      if (num !== undefined) {
        row.baseRate = num;
      } else if (String(v).trim() !== '') {
        hasUnparsedRate = true;
      }
    } else if (target === 'sa') {
      const sv = String(v).trim().toLowerCase();
      row.sa = sv === 'true' || sv === 'ja' || sv === 'yes' || sv === '1' || sv === 'y';
    } else if (target === 'expectedStartDate') {
      let iso: string | undefined;
      if (v instanceof Date) {
        iso = v.toISOString();
      } else if (typeof v === 'number') {
        // Excel serial date
        const date = XLSX.SSF.parse_date_code(v);
        if (date) {
          const d = new Date(Date.UTC(date.y, (date.m || 1) - 1, date.d || 1));
          iso = d.toISOString();
        }
      } else if (typeof v === 'string' && v.trim()) {
        const guess = new Date(v);
        if (!isNaN(guess.getTime())) iso = guess.toISOString();
      }
      if (iso) row.expectedStartDate = iso;
    } else {
      row[target as StringImportField] = String(v).trim();
    }
  }

  return { row, hasIgnoredMarginColumn, hasUnparsedRate };
}
